import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useScript from 'react-script-hook/lib/use-script'
import { match } from 'ts-pattern'

import { datadogRum } from '@datadog/browser-rum'
import { EventSource, InternalEventType } from '@frame/lib/api/events/types'
import {
  ArgyleConnectionStatus,
  ArgylePrepData,
  ConnectorId,
  DirectTask,
  TrueworkJsStrategy,
} from '@frame/lib/api/tasks/types'
import { NO_ARGYLE_ITEM_ID_FOUND_IDENTIFIER } from '@frame/lib/constants'
import { useRootContext } from '@frame/lib/context'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { useStore } from '@frame/lib/stores/useStore'
import { routes } from '@frame/routes'
import { LoginItem } from '@script/types'

import { useAutoNavigate } from '../useAutoNavigate'
import { useLoginItems } from '../useLoginItems'
import { useMonitoring } from '../useMonitoring'
import {
  AccountData,
  Argyle,
  ArgyleLinkInstance,
  CloseMethod,
  FormData,
  InternalArgyleEvents,
  LinkErrorData,
  UIEvent,
  UIEventName,
} from './types'

function handleArgyleEvent(name: string, properties?: Record<string, unknown>) {
  // The name prefix and connector are redundant because I'm not sure which will be easier to use in Datadog.
  datadogRum.addAction(`argyle/${name}`, {
    ...properties,
    connector: ConnectorId.ARGYLE,
  })
}

function getItemId(task: DirectTask, loginItemIds: LoginItem[]) {
  if (loginItemIds.length === 1) {
    return loginItemIds[0].platformId
  }

  return task?.connector_company_identifier ?? NO_ARGYLE_ITEM_ID_FOUND_IDENTIFIER
}

const ARGYLE_SRC = 'https://plugin.argyle.com/argyle.web.v5.js'
const SANDBOX_BASE = 'https://api-sandbox.argyle.com/v2'
const ARGYLE_FLOW_ID = 'TCMPWKKG'

const CACHED_LINK_INSTANCE: {
  [k: string]: ArgyleLinkInstance | null
} = {
  [NO_ARGYLE_ITEM_ID_FOUND_IDENTIFIER]: null,
}
declare namespace window {
  const Argyle: Argyle
}

type Args = {
  data: ArgylePrepData
  task: DirectTask
}

export function useArgyleLink({ data, task }: Args) {
  const navigate = useNavigate()
  const { mutateAndNavigate } = useAutoNavigate()
  const monitor = useMonitoring()
  const { handleEvent } = useEventHandler()
  const { actions, api } = useRootContext()
  const { isMultiEmployer, consumerInitiated } = useStore(({ session }) => ({
    isMultiEmployer: Boolean(session?.max_employments && session.max_employments > 1),
    consumerInitiated: session?.consumer_initiated,
  }))
  const loginItemIds = useLoginItems()
  const [createdLink, setCreatedLink] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isPendingConnection, setIsPendingConnection] = useState(false)
  const cantFindItemClickedRef = useRef(false)
  const itemId = getItemId(task, loginItemIds)

  const handleAccountConnected = useCallback(
    async (data: AccountData) => {
      handleArgyleEvent('onAccountConnected', data)

      CACHED_LINK_INSTANCE[itemId]?.close()
      setIsLoading(true)

      await actions.completeDirectTask({
        directTaskId: task.id,
        completeDirectTaskBody: {
          strategy: TrueworkJsStrategy.CREDENTIALS,
          info: {
            connector_id: ConnectorId.ARGYLE,
            employer_name: data.itemId,
            metadata: data,
          },
        },
        multiEmployer: isMultiEmployer,
      })

      navigate(routes.root, { replace: true, state: { fromLoginSuccess: true, taskId: task.id } })

      handleEvent(InternalEventType.LOGIN_SUCCEEDED, {
        partner_event: { name: ConnectorId.ARGYLE, ...data },
      })
      if (isMultiEmployer) {
        handleEvent(InternalEventType.COMPLETED_EMPLOYER_VERIFICATION_FLOW, {
          partner_event: { name: ConnectorId.ARGYLE },
        })
      }
    },
    [task, itemId, isMultiEmployer, actions, navigate, handleEvent]
  )

  const handleAccountCreated = useCallback((data: AccountData) => {
    handleArgyleEvent('onAccountCreated', data)
  }, [])

  const handleAccountError = useCallback(
    (data: AccountData) => {
      handleArgyleEvent('onAccountError', data)

      handleEvent(InternalEventType.LOGIN_FAILED, {
        partner_event: { name: ConnectorId.ARGYLE, ...data },
      })

      if (isMultiEmployer) {
        handleEvent(InternalEventType.FAILED_EMPLOYER_VERIFICATION_FLOW, {
          partner_event: { name: ConnectorId.ARGYLE },
        })
      }
    },
    [isMultiEmployer, handleEvent]
  )

  const handleAccountRemoved = useCallback((data: AccountData) => {
    handleArgyleEvent('onAccountRemoved', data)
  }, [])

  const handleCantFindItemClicked = useCallback(() => {
    handleArgyleEvent('onCantFindItemClicked')
    cantFindItemClickedRef.current = true
  }, [cantFindItemClickedRef])

  const handleClose = useCallback(() => {
    handleArgyleEvent('onClose')
  }, [])

  const handleCloseAfterCantFindItemClicked = useCallback(async () => {
    if (consumerInitiated) {
      if (task?.is_external_employment) {
        await actions.skipDirectTask(task.id)
        mutateAndNavigate()
      } else {
        const params = new URLSearchParams({ q: task?.employer_name ?? '' })
        navigate(`${routes.confirmEmployer}/${task.id}?${params.toString()}`)
      }
    } else {
      navigate(routes.employerNotFound)
    }
  }, [actions, consumerInitiated, task, mutateAndNavigate, navigate])

  const checkAccountStatus = useCallback(
    async (acctId?: string) => {
      const accountId = acctId || data?.account_id

      if (accountId) {
        try {
          const { data: accountData } = await api.tasks.getConnectorAccount(accountId, task.id)
          /*
          Argyle shows a "Connecting pending" screen to the user if the account isn't connected yet.
          There is no more action for the user to take at this point and we can consider the task complete.
          However, there is no UI callback that is sent for this screen, so we need to query the account to get the status.
          The "connecting" status is the corresponding account status for this screen.
        */
          if (
            accountData?.connection_status &&
            [ArgyleConnectionStatus.CONNECTING, ArgyleConnectionStatus.CONNECTED].includes(
              accountData?.connection_status
            ) &&
            !isPendingConnection
          ) {
            await actions.completeDirectTask({
              directTaskId: task.id,
              completeDirectTaskBody: {
                strategy: TrueworkJsStrategy.CREDENTIALS,
                info: {
                  connector_id: ConnectorId.ARGYLE,
                  employer_name: itemId,
                  metadata: {
                    account_id: data.account_id,
                    userId: data.user_id,
                    itemId,
                  },
                },
              },
              multiEmployer: isMultiEmployer,
            })

            setIsPendingConnection(true)
            return
          }

          setIsPendingConnection(false)
        } catch {
          setIsPendingConnection(false)
        }
      }

      return false
    },
    [actions, isMultiEmployer, api.tasks, data.account_id, data.user_id, task.id, itemId, isPendingConnection]
  )

  const closeAndNavigate = useCallback(
    async (closeMethod: CloseMethod, acctId?: string) => {
      await actions.getSession()
      await checkAccountStatus(acctId)

      if (isPendingConnection) {
        navigate(routes.root, { replace: true, state: { fromLoginSuccess: true, taskId: task.id } })
        monitor({
          event_name: InternalArgyleEvents.LOGIN_SUCCEEDED,
          event_source: EventSource.ARGYLE,
          partner_event: data,
        })
        if (isMultiEmployer) {
          handleEvent(InternalEventType.COMPLETED_EMPLOYER_VERIFICATION_FLOW, {
            partner_event: { name: ConnectorId.ARGYLE },
          })
        }
        return
      }

      const defaultAction = () => {
        navigate(routes.confirmExit)
        handleEvent(InternalEventType.ATTEMPTED_EXIT, { partner_event: { name: ConnectorId.ARGYLE } })
      }

      if (cantFindItemClickedRef.current) {
        handleCloseAfterCantFindItemClicked()
      } else {
        match(closeMethod)
          .with('x', () => {
            navigate(-1)
          })
          .with('footer_button', () => {
            mutateAndNavigate()
          })
          .with(null, () => {
            // The close method is null when manually closing Argyle via the close() method
            // The logic to perform when closing should happen at the call site
            return
          })
          .otherwise(() => {
            defaultAction()
          })
      }
    },
    [
      actions,
      isPendingConnection,
      isMultiEmployer,
      mutateAndNavigate,
      navigate,
      handleCloseAfterCantFindItemClicked,
      handleEvent,
      monitor,
      checkAccountStatus,
      task.id,
      data,
    ]
  )

  const handleDocumentsSubmitted = useCallback((data: FormData) => {
    handleArgyleEvent('onDocumentsSubmitted', data)
  }, [])

  const handleFormSubmitted = useCallback((data: FormData) => {
    handleArgyleEvent('onFormSubmitted', data)
  }, [])

  const handleUIEvent = useCallback(
    async ({ name, properties }: UIEvent) => {
      handleArgyleEvent(`onUIEvent/${name}`, properties ? properties : undefined)

      if (name === UIEventName.LINK_CLOSED) {
        closeAndNavigate(properties?.closeMethod)
      }

      if (name === UIEventName.ACCOUNT_STATUS_OPENED) {
        closeAndNavigate(null, properties.accountId)
      }

      monitor({
        event_name: name,
        event_source: EventSource.ARGYLE,
        partner_event: properties,
      })
    },
    [closeAndNavigate, monitor]
  )

  const handleError = useCallback(
    async (data: LinkErrorData) => {
      handleArgyleEvent('onError', data)

      CACHED_LINK_INSTANCE[itemId]?.close()

      const failedTask = await actions.completeDirectTask({
        directTaskId: task.id,
        completeDirectTaskBody: {
          strategy: TrueworkJsStrategy.CREDENTIALS,
          info: {
            connector_id: ConnectorId.ARGYLE,
            employer_name: null,
            metadata: data,
          },
          failed: true,
        },
        multiEmployer: isMultiEmployer,
      })

      if (failedTask) {
        navigate(routes.connectionError, { replace: true, state: { task: failedTask } })
      }

      monitor({
        event_name: InternalArgyleEvents.ERROR_OPENED,
        event_source: EventSource.ARGYLE,
        partner_event: data,
      })
    },
    [actions, task.id, itemId, isMultiEmployer, navigate, monitor]
  )

  const handleTokenExpired = useCallback(() => {
    handleArgyleEvent('onTokenExpired')
  }, [])

  const createLink = useCallback(() => {
    const sandbox = data.api_host === SANDBOX_BASE
    const accountId = data.account_id
    const userToken = data.user_token

    // eslint-disable-next-line no-console
    console.log('Creating Argyle link instance')
    const link = window.Argyle.create({
      sandbox,
      accountId,
      userToken,
      flowId: ARGYLE_FLOW_ID,
      ...(itemId !== NO_ARGYLE_ITEM_ID_FOUND_IDENTIFIER && { items: [itemId] }),
      onAccountConnected: (data) => handleAccountConnected(data),
      onAccountCreated: handleAccountCreated,
      onAccountError: handleAccountError,
      onAccountRemoved: handleAccountRemoved,
      onCantFindItemClicked: handleCantFindItemClicked,
      onClose: handleClose,
      onDocumentsSubmitted: handleDocumentsSubmitted,
      onFormSubmitted: handleFormSubmitted,
      onUIEvent: handleUIEvent,
      onError: handleError,
      onTokenExpired: handleTokenExpired,
    })

    if (!CACHED_LINK_INSTANCE[itemId]) {
      CACHED_LINK_INSTANCE[itemId] = link
    }

    setCreatedLink(true)
  }, [
    data.account_id,
    data.api_host,
    data.user_token,
    itemId,
    handleAccountConnected,
    handleAccountCreated,
    handleAccountError,
    handleAccountRemoved,
    handleCantFindItemClicked,
    handleClose,
    handleDocumentsSubmitted,
    handleFormSubmitted,
    handleUIEvent,
    handleError,
    handleTokenExpired,
  ])

  const [loading, error] = useScript({
    src: ARGYLE_SRC,
    checkForExisting: true,
    onload: () => {
      createLink()
    },
  })

  useEffect(() => {
    if (!loading && !error && !createdLink) {
      createLink()
    }
  }, [loading, error, createdLink, createLink])

  return { loading: isLoading || loading, error, linkInstance: CACHED_LINK_INSTANCE[itemId] }
}
